<template>
  <div class="live-streaming-container" v-if="eventLoaded">
    <TopBar>
      <div
        class="d-flex flex-row align-items-center justify-content-between h-100"
      >
        <div class="d-flex align-items-center pl-3 h-100">
          <span class="c-charcoal d-none d-sm-inline-block">{{
            event.event_name
          }}</span>
          <span class="pl-3" v-if="!displaySetup">{{ sessionDuration }}</span>
        </div>
        <div class="d-flex align-items-center h-100" v-if="!shouldDisplaySetup">
          <button
            class="btn top-right-btn mr-2"
            @click="toggleSetup"
            v-if="canStream"
          >
            <i class="fal fa-wrench"></i>
          </button>
          <button
            class="btn top-right-btn"
            :class="{ 'chat-opened': !isChatClosed }"
            @click="toggleChat"
          >
            <i class="fal fa-comments-alt"></i>
            <span v-if="newMessagesCount > 0" class="msg-count">{{
              newMessagesCount
            }}</span>
          </button>
        </div>
      </div>
    </TopBar>
    <div
      v-if="shouldDisplaySetup"
      class="container h-100 d-flex align-items-center justify-content-center"
    >
      <OpenTokPreStreamSetup
        class="bg-white p-3"
        :buttonText="$t('EVENTS.BUTTON.START_STREAMING')"
        @close="setStreamingProps"
      />
    </div>
    <div v-else class="container-fluid stream">
      <div class="row h-100">
        <OpenTokSession
          class="col-sm-12 m-0 p-0 h-100 d-flex flex-row live-session"
          :class="{ 'chat-expanded': !isChatClosed }"
          :sessionId="event.streaming_session_id"
          :token="event.streaming_token"
          :name="event.event_name"
          :isStartStopSessionInProgress="isStartStopSessionInProgress"
          :isSetupClosed="isSetupClosed"
          :canStream="canStream"
          :logoUrl="studioLogo"
          @stop="stopStreamingSession(true)"
        />
        <HMChat
          v-if="user"
          class="chat-container col-sm-12 m-0 p-0 h-100"
          :class="{ closed: isChatClosed }"
          :userId="`${user.user_id}`"
          :isTeacher="true"
          :userName="user.user_full_name"
          :userImage="user.avatar"
          :userToken="event.stream_chat_token"
          :channelId="event.chat_channel_id"
          :channelName="event.event_name"
          @newMessage="chatNewMessage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import OpenTokSession from '@/web/views/admin/events/streaming/OpenTokSession';
import { mapGetters } from 'vuex';
import { errorToMessage } from '@/core/utils/errors';
import sleep from '@/web/utils/sleep';
import HMChat from '@/web/components/chat/HMChat';
import TopBar from '@/web/components/navigation/TopBar';
import OpenTokPreStreamSetup from '@/web/views/admin/events/streaming/OpenTokPreStreamSetup';
import Vue from 'vue';
import { EVENT_TYPE_PRIVATE } from '@/core/utils/events';
import { getStudioURL } from '@/web/router/custom-domain-utils';

export default Vue.extend({
  name: 'StudioOpenTokLiveStream',
  components: {
    OpenTokSession,
    HMChat,
    TopBar,
    OpenTokPreStreamSetup,
  },
  computed: {
    ...mapGetters({
      event: 'event/event',
      user: 'user/user',
      studio: 'studio/currentStudio',
    }),
    studioLogo() {
      return (
        this.studio?.logo?.storage_url ?? this.studio?.logo?.embedded_image_url
      );
    },
    canStream() {
      return this.event?.event_type === EVENT_TYPE_PRIVATE;
    },
    shouldDisplaySetup() {
      if (!this.canStream) return false;
      return this.displaySetup;
    },
  },
  data() {
    return {
      displaySetup: true,
      streamingProps: {
        advancedAudioProcessing: true,
        useContainMode: true,
      },
      isChatClosed: true,
      isSetupClosed: true,
      sessionStartTime: moment(),
      sessionDuration: '',
      stopwatchInterval: null,
      newMessagesCount: 0,
      eventLoaded: false,
      isStartStopSessionInProgress: false,
      sessionStopped: false,
    };
  },
  methods: {
    setStreamingProps(props) {
      this.displaySetup = false;
      this.sessionStartTime = moment();
      this.streamingProps = props;
    },
    eventDuration() {
      const duration = moment.duration(moment().diff(this.sessionStartTime));
      this.sessionDuration = moment()
        .hour(duration.hours())
        .minute(duration.minutes())
        .second(duration.seconds())
        .format('HH:mm:ss');
    },
    async stopStreamingSession(goBack) {
      if (goBack) {
        this.$router.go(-1);
      }
    },
    chatNewMessage() {
      if (this.isChatClosed) {
        this.newMessagesCount += 1;
      }
    },
    toggleChat() {
      this.isChatClosed = !this.isChatClosed;
      if (!this.isChatClosed) {
        this.newMessagesCount = 0;
      }
    },
    toggleSetup() {
      this.isSetupClosed = !this.isSetupClosed;
    },
  },
  async mounted() {
    sleep.prevent();
    try {
      await this.$store.dispatch('event/getStudioEventDetails', {
        studioURL: getStudioURL(this.$route),
        eventId: this.$route.params.event_id,
      });
      this.eventLoaded = true;
    } catch (error) {
      console.error(error);
    }
    window.onbeforeunload = () => {
      // close the session when user close the tab or browser
      sleep.allow();
      this.stopStreamingSession(false);
    };
    window.onunload = () => {
      sleep.allow();
      this.stopStreamingSession(false);
    };
    this.stopwatchInterval = setInterval(this.eventDuration, 1000);
  },
  beforeDestroy() {
    clearInterval(this.stopwatchInterval);
    sleep.allow();
    this.stopStreamingSession(false);
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    window.onbeforeunload = () => {};
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    window.onunload = () => {};
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.live-streaming-container {
  background-color: $light-gray;
  min-height: 100vh;
  height: 100%;
  max-height: 100%;

  .stream {
    max-height: unquote('calc(100vh - #{$streaming-topbar-height})');
    height: unquote('calc(100vh - #{$streaming-topbar-height})');
  }

  .live-session {
    width: 100%;
    max-width: 100%;

    &.chat-expanded {
      width: 66%;
      max-width: 66%;

      @media screen and (max-width: 760px) {
        width: 100%;
        max-width: 100%;
      }
    }
  }

  .top-right-btn {
    background-color: $white;
    color: $dark-gray;
    border-radius: 0;
    border-top: none;
    border-right: none;
    border-bottom: none;
    border-left: 1px solid $light-gray;
    padding: 0.5rem 1.5rem;

    &:hover {
      background-color: $medium-gray;
    }

    &.chat-opened {
      background-color: $charcoal;
      color: $white;
    }

    .msg-count {
      border-radius: 50%;
      width: 1rem;
      height: 1rem;
      background-color: $coral;
      color: white;
      font-size: 0.5rem;
      padding: 4px;
      position: absolute;
      top: 0.5rem;
      right: 1rem;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  .chat-container {
    position: relative;
    right: 0;
    width: 34%;
    max-width: 34%;
    height: unquote('calc(100vh - #{$streaming-topbar-height})');
    max-height: unquote('calc(100vh - #{$streaming-topbar-height})');
    transition: width ease-in-out 0.3s;

    @media screen and (max-width: 1200px) {
      width: 34%;
      max-width: 34%;
    }

    @media screen and (max-width: 992px) {
      width: 34%;
      max-width: 34%;
    }

    @media screen and (max-width: 760px) {
      width: 100%;
      max-width: 100%;
    }

    &.open {
    }

    &.closed {
      position: fixed;
      max-height: 0;
      width: 0;
      visibility: hidden;
    }
  }
}
</style>
